import React, { Fragment, useCallback, useContext, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import DragItemTypes from "../DragItemTypes";
import clsx from "clsx";
import { any, mergeRight, propEq } from "ramda";
import { capitalize } from "../../../util/stringFormat";
import { getType } from "../../../util/mediaUtil";
import { useSelected } from "../OrganizeProvider";
import { OrganizeContext } from "../organize-context";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import { notBlank } from "../../../util/ext";
import {
  useView,
  isUngroupedHome,
  isContentGroupTopLevelView,
} from "../organizeUtil";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
// import { ContextMenuContext } from "../OrganizeContextMenuProvider";

import Checkbox from "@material-ui/core/Checkbox";
// import IconButton from "@material-ui/core/IconButton";
// import Menu from "@material-ui/core/Menu";
// import MenuItem from "@material-ui/core/MenuItem";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";

import ContentCard from "../../content/browse/ContentCard";
import ContentOriginCard from "../../common/ContentOriginCard";
import ContentParentsCard from "../content-groups/card/ContentParentsCard";

const useStyles = makeStyles(theme => ({
  checkbox: {
    color: "inherit",
  },

  checkboxSelected: {
    color: "white",
  },

  contentCell: {
    paddingRight: theme.spacing(3),
  },

  contentTypeCell: {
    textTransform: "capitalize",
  },

  dragging: {
    opacity: 0.5,
  },

  icon: {
    color: theme.palette.primary.darker,
  },

  root: {
    cursor: "grab",
    "&$selected": {
      backgroundColor: theme.palette.primary.main,
      "&> *, &> p": {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },

    "&$selected:hover": {
      backgroundColor: theme.palette.primary.main,
      "&> *, &> p": {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
  },

  reviewTitle: {
    paddingLeft: theme.spacing(6),
  },

  selected: {},
}));

// const ActionMenu = () => {
//   const [anchorEl, setAnchorEl] = useState(null);
//   const handleMenuClick = event => {
//     event.stopPropagation();
//     event.preventDefault();
//     setAnchorEl(event.currentTarget);
//   };

//   const handleClose = event => {
//     event.stopPropagation();
//     setAnchorEl(null);
//   };
//   return (
//     <Fragment>
//       <IconButton onClick={handleMenuClick}>
//         <FontAwesomeIcon icon={faEllipsisVertical} />
//       </IconButton>
//       <Menu
//         id={`other-actions-for-anthology`}
//         anchorEl={anchorEl}
//         open={Boolean(anchorEl)}
//         onClose={handleClose}
//         getContentAnchorEl={null}
//         anchorOrigin={{
//           vertical: "top",
//           horizontal: "left",
//         }}
//         transformOrigin={{
//           vertical: "top",
//           horizontal: "right",
//         }}
//       >
//         <MenuItem onClick={e => e.stopPropagation()}>
//           Remove From Collection in this Anthology
//         </MenuItem>
//         <MenuItem onClick={e => e.stopPropagation()}>
//           Add to Collection in this Anthology
//         </MenuItem>
//       </Menu>
//     </Fragment>
//   );
// };

const DraggableRow = props => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const view = useView();

  const {
    content,
    loading,
    parent,
    parentMediaList,
    handleOpenViewerClick,
    setLoading,
    searchState,
    sourceFilter,
  } = props;

  const {
    selectedContent,
    updateActiveSelectedMediaType,
    updateSelectedContent,
  } = useSelected();

  const handleRowClick = content => event => {
    updateSelectedContent(content);
    updateActiveSelectedMediaType("content");
  };

  const [, drag, preview] = useDrag(
    () => ({
      type: DragItemTypes.CONTENT,
      item: {
        id: content.id,
        mediaType: "content",
        parent,
        prevMediaList: parentMediaList,
      },
      collect: monitor => ({
        isDragging: monitor.isDragging(),
        canDrag: monitor.canDrag(),
      }),
    }),
    [content, parent, parentMediaList],
  );

  const context = useContext(OrganizeContext);

  const handleDragStart = () => {
    if (!any(propEq("id", content.id), selectedContent)) {
      updateSelectedContent({ id: content.id, parent });
    }
    context.updateIsDragging(true);
  };

  const handleDragEnd = () => context.updateIsDragging(false);

  // This is required for creating a custom drag overlay
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const handleContentEditClick = useCallback(
    event => {
      event.stopPropagation();
      const { pathname, search } = location;
      const fromPath = notBlank(search) ? pathname + search : pathname;
      history.push(`/creator/content/${content.id}`, {
        from: fromPath,
        searchState,
      });
    },
    [content.id, searchState, location, history],
  );

  const handleViewerClick = event => {
    event.stopPropagation();
    handleOpenViewerClick(content);
  };

  return (
    <Fragment>
      <TableRow
        ref={drag}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        id={content.id}
        classes={{
          root: clsx(classes.root, {
            [classes.dragging]:
              context.state.isDragging &&
              any(propEq("id", content.id), selectedContent),
          }),
        }}
        onClick={handleRowClick(
          mergeRight(content, { parent, prevMediaList: parentMediaList }),
        )}
        hover
        tabIndex={-1}
        role="checkbox"
        aria-checked={any(propEq("id", content.id), selectedContent)}
        selected={any(propEq("id", content.id), selectedContent)}
      >
        <TableCell padding="checkbox" align="center">
          <Checkbox
            color="primary"
            className={clsx(classes.checkbox, {
              [classes.selectedCheckbox]: any(
                propEq("id", content.id),
                selectedContent,
              ),
            })}
            checked={any(propEq("id", content.id), selectedContent)}
            inputProps={{
              "aria-labelledby": "content-checkbox-" + content.id,
            }}
          />
        </TableCell>

        <TableCell
          key={"title:" + content.id}
          align="left"
          className={classes.contentCell}
        >
          <ContentCard
            handleThumbnailClick={handleViewerClick}
            handleTitleClick={handleContentEditClick}
            loading={loading}
            content={content}
            selected={any(propEq("id", content.id), selectedContent)}
          />
        </TableCell>

        <TableCell key={"date-updated:" + content.id} align="left">
          {new Date(
            isContentGroupTopLevelView(view)
              ? content.auditAdded
              : content.auditUpdated,
          ).toLocaleDateString()}
        </TableCell>

        <TableCell
          key={"content-type:" + content.id}
          align="left"
          className={classes.contentTypeCell}
        >
          {capitalize(getType(content))}
        </TableCell>

        <TableCell
          onClick={e => e.stopPropagation()}
          key={"origin:" + content.id}
          align="left"
        >
          <ContentOriginCard content={content} sourceFilter={sourceFilter} />
        </TableCell>

        {!isUngroupedHome(view) && (
          <TableCell key={"included-in:" + content.id} align="left">
            <ContentParentsCard content={content} setLoading={setLoading} />
          </TableCell>
        )}

        {/* {equals(view, "anthology") && (
          <TableCell align="center">
            <ActionMenu />
          </TableCell>
        )} */}
      </TableRow>
    </Fragment>
  );
};

export default React.memo(DraggableRow);
